import { useState } from "react";
import Lottie from "lottie-react";
import { GetFiatInstanceDetailsData } from "../../services/models/PrimeFiat";
import { clsxm } from "../../utils/clxm/clxm";
import { getStatusInfo } from "../../utils/orderStatus";
import { getTransactionStatusIcon } from "../../utils/transactionStatus";

function formatAmount(value?: number | string, digits = 2) {
  const num = Number(value);
  if (Number.isNaN(num)) return "-";
  return num.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function currencyPrefix(symbol?: string) {
  if (symbol === "CAD") return "$";
  return "€";
}

interface OrderStatusCardProps {
  instanceData: GetFiatInstanceDetailsData;
  region?: string;
}

function OrderStatusCard({ instanceData, region }: OrderStatusCardProps) {
  const [showInverseRate, setShowInverseRate] = useState(false);
  const quotedRate = (() => {
    const rate = Number(instanceData.rate);
    if (!Number.isFinite(rate) || rate === 0) return undefined;
    const cryptoPerFiat =
      instanceData.fiatCurrencySymbol === "CAD" ? 1 / rate : rate;
    return showInverseRate ? 1 / cryptoPerFiat : cryptoPerFiat;
  })();
  const rateBaseSymbol = showInverseRate
    ? instanceData.cryptoCurrencySymbol
    : instanceData.fiatCurrencySymbol;
  const rateQuoteSymbol = showInverseRate
    ? instanceData.fiatCurrencySymbol
    : instanceData.cryptoCurrencySymbol;
  const info = getStatusInfo(region, instanceData);
  const status = getTransactionStatusIcon(info.statusKey);
  const showKycLink =
    instanceData.kycStatus === "NOT STARTED" && !!instanceData.kycSessionUrl;

  return (
    <div className="flex flex-col">
      <div
        className="px-[24px] pt-[24px] pb-[28px] text-white"
        style={{
          background:
            "linear-gradient(73deg, #615998 14.91%, #352D6A 59.27%, #201D32 99.06%)",
        }}
      >
        <div
          className={clsxm(
            "rounded-[10px] px-[14px] py-[12px] flex flex-col gap-[8px]",
            status.bgColor,
          )}
        >
          <div className="flex items-center justify-between gap-[12px]">
            <div className="flex items-center gap-[8px] min-w-0">
              <div className="w-[28px] h-[28px] shrink-0 flex items-center justify-center">
                <Lottie
                  animationData={status.lottieData}
                  loop={
                    status.statusKind === "pending" ||
                    status.statusKind === "in_progress"
                  }
                  autoplay
                  style={{ width: 28, height: 28 }}
                />
              </div>
              <span
                className={clsxm(
                  "text-[14px] font-semibold truncate",
                  status.iconColor,
                )}
              >
                Order Status
              </span>
            </div>
            <span
              className={clsxm(
                "text-[14px] font-semibold text-right shrink-0",
                status.iconColor,
              )}
            >
              {info.title}
            </span>
          </div>
          <p
            className={clsxm(
              "text-[11px] leading-[1.45] font-normal",
              status.iconColor,
            )}
          >
            {info.description}
          </p>
        </div>

        {showKycLink ? (
          <a
            href={instanceData.kycSessionUrl || undefined}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-[12px] inline-flex items-center gap-[6px] text-white text-[12px] font-medium underline"
          >
            Start KYC Verification
            <span className="material-symbols-outlined text-[14px]">
              open_in_new
            </span>
          </a>
        ) : null}

        {region === "cad" &&
        [2, 3, 4].includes(instanceData.eventId) &&
        instanceData.ReferenceNumber ? (
          <div className="mt-[12px] text-white/80 text-[10px] space-y-[2px]">
            <p>Reference: {instanceData.ReferenceNumber}</p>
            {instanceData.TransactionNumber ? (
              <p>Transaction: {instanceData.TransactionNumber}</p>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="px-[24px] pt-[20px] pb-[32px] bg-bg-page-body">
        <h3 className="text-text-subtle text-[20px] font-bold">
          Order summary
        </h3>
        <div className="mt-[14px] space-y-[10px] text-[14px]">
          <div className="flex justify-between gap-[12px]">
            <span className="text-text-subtle text-[12px] font-medium">
              Purchase Amount
            </span>
            <span className="font-semibold text-text-label">
              {currencyPrefix(instanceData.fiatCurrencySymbol)}
              {formatAmount(instanceData.fiatAmount)}
            </span>
          </div>
          <div className="border-t border-border-subtle" />
          <div className="flex justify-between gap-[12px]">
            <span className="text-text-subtle text-[12px] font-medium">
              Exchange rate
            </span>
            <button
              type="button"
              onClick={() => setShowInverseRate((prev) => !prev)}
              className="inline-flex items-center justify-end gap-[4px] font-semibold text-text-label text-right"
              aria-label="Show the inverse exchange rate"
            >
              <span>1 {rateBaseSymbol}</span>
              <span className="material-symbols-outlined text-[14px]">
                sync_alt
              </span>
              <span>
                {formatAmount(quotedRate, 4)} {rateQuoteSymbol}
              </span>
            </button>
          </div>
          <div className="border-t border-border-subtle" />
          <div className="flex justify-between gap-[12px]">
            <span className="text-text-subtle text-[12px] font-medium">
              You receive
            </span>
            <span className="font-semibold text-text-label">
              {formatAmount(
                instanceData?.fiatCurrencySymbol === "CAD"
                  ? instanceData?.toReleaseAmount
                  : instanceData?.cryptoAmount,
              )}{" "}
              {instanceData.cryptoCurrencySymbol}
            </span>
          </div>
          <div className="border-t border-border-subtle" />
          {instanceData?.fees && (
            <div className="flex justify-between gap-[12px]">
              <span className="text-text-subtle text-[12px] font-medium">
                Service fee
              </span>
              <span className="font-semibold text-text-label">
                {currencyPrefix(instanceData.fiatCurrencySymbol)}
                {formatAmount(instanceData.fees)}
              </span>
            </div>
          )}
          {instanceData?.fees && (
            <div className="border-t border-border-subtle" />
          )}
          <div className="flex justify-between gap-[12px]">
            <span className="text-text-subtle text-[12px] font-medium">
              Final Payment Amount
            </span>
            <span className="font-semibold text-text-label">
              {currencyPrefix(instanceData.fiatCurrencySymbol)}
              {formatAmount(instanceData.fiatAmount)}
            </span>
          </div>
          <div className="border-t border-[#232025]" />
          <div className="flex justify-between gap-[12px]">
            <span className="text-text-subtle text-[12px] font-medium shrink-0">
              Order ID
            </span>
            <span className="font-semibold text-text-label text-right break-all">
              {instanceData.merchantOrderId}
            </span>
          </div>
          <div className="border-t border-border-subtle" />
          <div className="flex justify-between gap-[12px]">
            <span className="text-text-subtle text-[12px] font-medium shrink-0">
              Payment ID
            </span>
            <span className="font-semibold text-text-label text-right break-all">
              {instanceData.instanceId}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OrderStatusCard;
